import {
  CARD_SPECS, cardsOfPage, getDef, isCustomRendered, isSecret, wiringOf,
  type CardBtn, type CardRow, type CardSpec, type WireNote
} from './settings-schema.ts';
import type { SettingDep } from '@jyfk/settings/types';
import { buttonStatus, cardFix, customBlock, fixedNote, fixedSect, type CardFix } from './blocks.ts';
import { T, type Trilingual } from './i18n.ts';

/**
 * 一页设置怎么画：每张卡片画哪些行、哪里标「还没做」、整张卡要不要一条横幅。
 *
 * 纯函数，不碰 React —— 这一层决定的是「界面说的话对不对」，
 * 要能不开浏览器就测（见 card-plan.test.ts）。SettingsPage 只负责把它画出来。
 *
 * ── 判定规则 ────────────────────────────────────────────
 * 一张卡片上每样东西（设置项、定制块、按钮）要么是「在起作用的」，要么是
 * 「还没做 / 桌面版才有 / 在别处」。**一样起作用的都没有**，整张卡就挂一条横幅，
 * 行上不再逐条重复标；有起作用的，就只在没接上的那几行上标。
 */

export type Pending = 'soon' | 'desktop';

export type PlanItem =
  /** 一个设置项。`tag` 非空 = 这一行要标出来（还没做 / 桌面版才有 / 在别处） */
  | { t: 'key'; key: string; tag: RowTag | null }
  /** 不收的凭据：只在用户以前存过时给一个「删掉」 */
  | { t: 'uncollected'; key: string }
  | { t: 'note'; text: Trilingual; dep: SettingDep | null }
  | { t: 'sect'; text: Trilingual; dep: SettingDep | null }
  /** 网页版做了的按钮（画成真按钮） */
  | { t: 'buttons'; btns: CardBtn[] }
  /** 在别的页面上：画一行指路 */
  | { t: 'pointer'; text: Trilingual }
  | {
    t: 'fold';
    id: string;
    label: Trilingual;
    items: PlanItem[];
    /** 这一组全都没接上时的说明 */
    banner: Trilingual | null;
    pending: PendingLine[];
  };

export interface RowTag {
  status: WireNote['status'];
  text: Trilingual;
}

export interface PendingLine {
  status: Pending;
  labels: Trilingual[];
}

export interface CardBanner {
  status: WireNote['status'];
  text: Trilingual;
}

export interface CardPlan {
  card: CardSpec;
  fix: CardFix | null;
  /** 整张卡由一个网页版组件代替（布局自定义、导航）。值是定制块的名字 */
  whole: string | null;
  /** 画在这张卡前面的定制卡片（配色、强调色、界面语言）的键 */
  extras: string[];
  items: PlanItem[];
  /** 卡片末尾的「还没做：……」「桌面版才有：……」 */
  pending: PendingLine[];
  banner: CardBanner | null;
  sub: Trilingual | null;
  /** 画出输入框的设置项（含折叠组里的、含 extras）—— 计数和「恢复默认」用 */
  inputKeys: string[];
  /** 其中网页版还不起作用的 */
  unwiredInputs: string[];
  /** 什么都不用画（比如订阅页那几张全由上面的真界面代替的卡） */
  empty: boolean;
}

const DEFAULT_TAG: Readonly<Record<WireNote['status'], Trilingual>> = {
  soon: T('网页版还没接上：存得下，暂时不起作用。', '網頁版還沒接上：存得下，暫時不起作用。', 'Not wired on the web yet: it saves but has no effect.'),
  desktop: T('桌面版才有，网页版不起作用。', '桌面版才有，網頁版不起作用。', 'Desktop only; no effect on the web.'),
  elsewhere: T('网页版在别处做了，这一项不起作用。', '網頁版在別處做了，這一項不起作用。', 'Handled elsewhere on the web; this setting has no effect.')
};

export function bannerDefault(status: WireNote['status'], hasInputs: boolean): Trilingual {
  if (status === 'soon') {
    return hasInputs
      ? T('这一块网页版还没做好。下面的设置可以先填好存着，做好之后按你存的生效。',
        '這一塊網頁版還沒做好。下面的設定可以先填好存著，做好之後按你存的生效。',
        'Not built on the web yet. You can fill these in now; they apply once it is built.')
      : T('这一块网页版还没做好。', '這一塊網頁版還沒做好。', 'Not built on the web yet.');
  }
  if (status === 'desktop') return T('这一块是桌面版的，网页版不需要。', '這一塊是桌面版的，網頁版不需要。', 'This is a desktop feature; the web version does not need it.');
  return T('这一块网页版放在别的地方了。', '這一塊網頁版放在別的地方了。', 'The web version handles this elsewhere.');
}

/** 一样东西「在起作用」还是某种没做 */
type Effect = 'live' | WireNote['status'];

interface Acc {
  items: PlanItem[];
  pending: Map<Pending, Trilingual[]>;
  effects: Effect[];
  /** 没接上的项自带的说明（选横幅文字用） */
  notes: Array<{ status: WireNote['status']; text: Trilingual }>;
  inputKeys: string[];
  unwiredInputs: string[];
  extras: string[];
  whole: string | null;
}

function newAcc(): Acc {
  return { items: [], pending: new Map(), effects: [], notes: [], inputKeys: [], unwiredInputs: [], extras: [], whole: null };
}

function addPending(acc: Acc, status: Pending, label: Trilingual | null): void {
  if (!label) return;
  const list = acc.pending.get(status) ?? [];
  if (!list.some(x => x.zh === label.zh)) list.push(label);
  acc.pending.set(status, list);
}

function pendingLines(acc: Acc): PendingLine[] {
  const out: PendingLine[] = [];
  for (const s of ['soon', 'desktop'] as const) {
    const l = acc.pending.get(s);
    if (l && l.length > 0) out.push({ status: s, labels: l });
  }
  return out;
}

/** 这一组东西的总体状态：一样起作用的都没有才算「没做」 */
function overall(effects: readonly Effect[]): WireNote['status'] | null {
  if (effects.length === 0 || effects.includes('live')) return null;
  if (effects.includes('soon')) return 'soon';
  if (effects.includes('elsewhere')) return 'elsewhere';
  return 'desktop';
}

/** 横幅用哪句话：和横幅同一状态的项里，说明占一半以上的那一句 */
function commonNote(notes: Acc['notes'], status: WireNote['status']): Trilingual | null {
  const same = notes.filter(n => n.status === status);
  if (same.length === 0) return null;
  const count = new Map<string, { n: number; text: Trilingual }>();
  for (const x of same) {
    const c = count.get(x.text.zh);
    if (c) c.n++;
    else count.set(x.text.zh, { n: 1, text: x.text });
  }
  const best = [...count.values()].sort((a, b) => b.n - a.n)[0]!;
  return best.n * 2 >= same.length ? best.text : null;
}

/**
 * 网页版用不上、也不用告诉用户的只读说明行（「开机自启：网页版不需要」这种）。
 * 老板 2026-10-30：桌面版才有的东西直接不画，别让用户看到
 */
const HIDE_ON_WEB: ReadonlySet<string> = new Set(['ge_auto', 'ge_tray']);

function walk(page: string, cardId: string, rows: readonly CardRow[], fix: CardFix | null, acc: Acc, inFold: boolean): void {
  for (const r of rows) {
    switch (r.t) {
      case 'key': {
        const def = getDef(r.key);
        if (!def) break;
        if (HIDE_ON_WEB.has(r.key)) break;
        if (isCustomRendered(def)) {
          /* 配色、强调色、界面语言：由 LookExtras 画成单独的卡片 */
          acc.extras.push(r.key);
          acc.inputKeys.push(r.key);
          acc.effects.push(wiringOf(r.key) ? wiringOf(r.key)!.status : 'live');
          break;
        }
        const w = wiringOf(r.key);
        if (!w) {
          acc.effects.push('live');
          acc.items.push({ t: 'key', key: r.key, tag: null });
          if (def.stored) acc.inputKeys.push(r.key);
          break;
        }
        /* 桌面版才有的：网页版一个字都不画，也不算进这张卡（老板 2026-10-30：别让用户看到） */
        if (w.status === 'desktop') break;
        acc.effects.push(w.status);
        if (w.note) acc.notes.push({ status: w.status, text: w.note });
        if (w.noInput) {
          /* 不画输入框。凭据要留一个口子：以前存过的，用户要能删掉 */
          if (isSecret(r.key)) acc.items.push({ t: 'uncollected', key: r.key });
          break;
        }
        acc.items.push({ t: 'key', key: r.key, tag: { status: w.status, text: w.note ?? DEFAULT_TAG[w.status] } });
        if (def.stored) {
          acc.inputKeys.push(r.key);
          acc.unwiredInputs.push(r.key);
        }
        break;
      }
      case 'note': {
        const text = fixedNote(fix, r.text);
        if (text) acc.items.push({ t: 'note', text, dep: r.dep });
        break;
      }
      case 'sect': {
        const text = fixedSect(fix, r.text);
        if (text) acc.items.push({ t: 'sect', text, dep: r.dep });
        break;
      }
      case 'btns': {
        const real: CardBtn[] = [];
        for (const b of r.btns) {
          const s = buttonStatus(page, b);
          if (s === 'built') { real.push(b); acc.effects.push('live'); }
          /* 被代替的（「保存」由整页的保存条代替）**不算「起作用」**：
             一张全是没做的设置、只剩一个「保存」按钮的卡片，照样是没做 */
          else if (s === 'replaced') continue;
          else if (s === 'elsewhere') acc.effects.push('elsewhere');
          else if (s === 'desktop') continue;
          else { acc.effects.push(s); addPending(acc, s, b.label); }
        }
        if (real.length > 0) acc.items.push({ t: 'buttons', btns: real });
        break;
      }
      case 'custom': {
        const b = customBlock(r.name);
        if (b.status === 'built') {
          acc.effects.push('live');
          /* 现在「做了」的定制块都是整张卡片（布局自定义、导航） */
          acc.whole = r.name;
        } else if (b.status === 'replaced') {
          /* 由别处的真界面代替（这一页上面的列表、边改边看的预览）：不算这张卡的内容，也不算没做 */
        } else if (b.status === 'elsewhere') {
          acc.effects.push('elsewhere');
          if (b.note) acc.items.push({ t: 'pointer', text: b.note });
        } else if (b.status === 'desktop') {
          /* 桌面版的定制块：不画 */
        } else {
          acc.effects.push(b.status);
          addPending(acc, b.status, b.label);
        }
        break;
      }
      case 'fold': {
        if (inFold) break; // 生成器不允许嵌套，这里只是兜底
        const sub = newAcc();
        if (r.name) {
          const b = customBlock(r.name);
          /* 整组是桌面版的：不画 */
          if (b.status === 'desktop') break;
          if (b.status === 'soon') {
            sub.effects.push(b.status);
            addPending(sub, b.status, b.label);
          } else if (b.status !== 'replaced') sub.effects.push('live');
        }
        walk(page, cardId, r.rows, fix, sub, true);
        /* 组里全是桌面版的东西（拿掉之后什么都不剩）：整组不画 */
        if (sub.items.length === 0 && sub.pending.size === 0 && sub.effects.length === 0) break;
        const st = overall(sub.effects);
        const label = r.label ?? T('');
        const banner = st === null ? null : (fix?.fold?.[label.zh] ?? commonNote(sub.notes, st) ?? bannerDefault(st, sub.inputKeys.length > 0));
        /* 整组都没接上：组里的行不再逐条标 */
        const items = st === null ? sub.items : sub.items.map(x => (x.t === 'key' ? { ...x, tag: null } : x));
        acc.items.push({ t: 'fold', id: foldId(page, cardId, label.zh), label, items, banner, pending: pendingLines(sub) });
        acc.effects.push(...sub.effects);
        acc.inputKeys.push(...sub.inputKeys);
        acc.unwiredInputs.push(...sub.unwiredInputs);
        break;
      }
    }
  }
}

/**
 * 去掉下面什么都没有的小标题。
 *
 * 小标题下面的项全是不画输入框的（比如钱包页「添加 / 编辑 Key」下面那三项 Key 在扩展里填），
 * 留着就是一个孤零零的标题，看起来像是内容没加载出来。
 * 不收的凭据那一行只在存过时才出现，不算「有东西」。
 */
function dropEmptySections(items: readonly PlanItem[]): PlanItem[] {
  const out: PlanItem[] = [];
  items.forEach((it, i) => {
    if (it.t !== 'sect') { out.push(it); return; }
    for (let j = i + 1; j < items.length; j++) {
      const n = items[j]!;
      if (n.t === 'sect') break;
      if (n.t !== 'uncollected') { out.push(it); return; }
    }
  });
  return out;
}

/** 折叠组的 id：「页面|卡片|组标题」。展开 / 收起的状态按它记 */
export function foldId(page: string, cardId: string, label: string): string {
  return page + '|' + cardId + '|' + label;
}

/** 全站所有折叠组的 id（「界面折叠」那三个按钮要用） */
export function allFoldIds(): string[] {
  const out: string[] = [];
  for (const [page, cards] of Object.entries(CARD_SPECS)) {
    for (const c of cards) {
      for (const r of c.rows) if (r.t === 'fold') out.push(foldId(page, c.id, r.label?.zh ?? ''));
    }
  }
  return out;
}

export function planCard(card: CardSpec): CardPlan {
  const fix = cardFix(card.page, card.id);
  const acc = newAcc();
  walk(card.page, card.id, card.rows, fix, acc, false);

  const st = overall(acc.effects);
  let banner: CardBanner | null = null;
  if (fix?.banner) banner = fix.banner;
  else if (st !== null && fix?.noBanner !== true) {
    banner = { status: st, text: commonNote(acc.notes, st) ?? bannerDefault(st, acc.inputKeys.length > 0) };
  }

  /* 有横幅的卡片：行上不再逐条重复标。横幅本身就是「在别处」时，卡片里的指路也不再重复 */
  let items = banner === null ? acc.items : acc.items.map(x => (x.t === 'key' ? { ...x, tag: null } : x));
  if (banner?.status === 'elsewhere') items = items.filter(x => x.t !== 'pointer');
  items = dropEmptySections(items);
  const sub = fix && 'sub' in fix ? (fix.sub ?? null) : card.sub;
  /* 整张卡是桌面版的（比如备用哨兵服务器）：不画（老板 2026-10-30） */
  const desktopOnly = banner?.status === 'desktop';
  const hasBody = !desktopOnly && (banner !== null || items.some(x => x.t !== 'uncollected') || acc.pending.size > 0);
  return {
    card,
    fix,
    whole: acc.whole,
    extras: acc.extras,
    items,
    pending: pendingLines(acc),
    banner,
    sub,
    inputKeys: acc.inputKeys,
    unwiredInputs: acc.unwiredInputs,
    /* 这张卡本身什么都不用画：全由网页版别处的真界面代替了（订阅页的套餐、订单那几张），
       或者整张卡只有一个定制块（配色、强调色 —— 它们由 extras 画成自己的卡片）。
       不收的凭据不算 —— 那一行只在存过时才出现 */
    empty: !hasBody
  };
}

/** 一页的全部卡片（藏掉的不算） */
export function planPage(page: string): CardPlan[] {
  return cardsOfPage(page)
    .filter(c => !cardFix(page, c.id)?.hide)
    .map(planCard);
}

/** 一页的计数：画出来的设置项、其中还不起作用的 */
export function pageCounts(plans: readonly CardPlan[]): { inputs: number; unwired: number } {
  let inputs = 0;
  let unwired = 0;
  for (const p of plans) {
    inputs += p.inputKeys.length;
    unwired += p.unwiredInputs.length;
  }
  return { inputs, unwired };
}
